import React, { useState, useRef } from "react";
import { Navigation } from "../components/Navigation";

import { VIDEO_URL } from "../constants/config";
import { COPY } from "../constants/copy";

interface VideoSectionProps {
  onNext: () => void;
  onPrevious: () => void;
}

export const VideoSection: React.FC<VideoSectionProps> = ({
  onNext,
  onPrevious,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle video end
  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-warmWhite dark:bg-darkBg transition-colors duration-400 py-16 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute w-96 h-96 bg-heartRed dark:bg-softRose opacity-5 rounded-full blur-3xl animate-pulse"
          style={{ top: "10%", right: "-5%" }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-softRose dark:bg-heartRed opacity-5 rounded-full blur-3xl animate-pulse"
          style={{ bottom: "10%", left: "-5%" }}
        ></div>
      </div>

      <div className="max-w-2xl w-full space-y-8 relative z-10">
        {/* Section title */}
        <div className="text-center space-y-3 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-light text-deepCharcoal dark:text-darkText">
            {COPY.video.title}
          </h2>
          <div className="h-px w-16 bg-gradient-to-r from-transparent via-heartRed to-transparent dark:via-softRose mx-auto"></div>
          <p className="text-sm text-mutedGray dark:text-gray-400 font-light tracking-wider">
            {COPY.video.subtitle}
          </p>
        </div>

        {/* Video container */}
        <div className="flex flex-col items-center justify-center">
          {/* Video player wrapper */}
          <div className="relative w-full max-w-sm animate-slide-up group">
            {/* Outer glow effect */}
            <div className="absolute -inset-2 bg-gradient-to-br from-heartRed via-softRose to-blushPink rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-500"></div>

            {/* Main video container */}
            <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl aspect-[9/16] flex items-center justify-center">
              {/* Video element */}
              <video
                preload="metadata"
                ref={videoRef}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={handleVideoEnd}
                poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1080 1920'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23E63946;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23FFB3B8;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1080' height='1920' fill='url(%23grad)'/%3E%3Ctext x='50%25' y='50%25' font-size='72' fill='white' text-anchor='middle' dominant-baseline='middle' font-family='system-ui'%3E60 Days%3C/text%3E%3C/svg%3E"
                className="w-full h-full object-cover"
              >
                {/* Local video file */}
                <source
                  src={VIDEO_URL}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>

              {/* Overlay gradient (when not playing) */}
              {!isPlaying && (
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black opacity-40"></div>
              )}

              {/* Play button overlay */}
              {!isPlaying && (
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center group/play transition-all duration-300"
                >
                  {/* Play icon background */}
                  <div className="relative flex items-center justify-center">
                    <div className="absolute w-20 h-20 bg-white opacity-20 rounded-full group-hover/play:opacity-30 group-active/play:opacity-10 transition-opacity duration-200"></div>
                    <div className="relative w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover/play:shadow-2xl group-hover/play:scale-110 transition-all duration-300">
                      {/* Play triangle */}
                      <svg
                        className="w-7 h-7 text-heartRed ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    </div>
                  </div>
                </button>
              )}

              {/* Close/Minimize button (when playing) */}
              {isPlaying && (
                <button
                  onClick={togglePlay}
                  className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-all duration-200 backdrop-blur-sm"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Video info cards below */}
          </div>
        </div>

        {/* Message */}
        <div
          className="text-center space-y-3 animate-fade-in"
          style={{ animationDelay: "0.3s" }}
        >
          <p className="text-sm text-mutedGray dark:text-gray-400 font-light italic">
            {isPlaying
              ? COPY.video.playingPrompt
              : COPY.video.playPrompt}
          </p>
        </div>

        {/* Button */}
        <div className="flex justify-center pt-4">
          <Navigation
            onNext={onNext}
            onPrevious={onPrevious}
            nextLabel={isPlaying ? COPY.video.finishButton : COPY.video.continueButton}
            showPrevious={true}
          />
        </div>

        {/* Subtle decoration */}
        <div className="text-center text-2xl text-softRose dark:text-blushPink opacity-20">
          ✦ ✦ ✦
        </div>
      </div>
    </div>
  );
};
